import { useState, useEffect, useCallback, useRef } from 'react';
import { accountsApi } from '../api';
import type { OrderDetailResponse, AccountDetailResponse } from '../types/api';
import type { OrderFilterState } from '../components/Orders/OrderFilters';

interface UseOrdersPollingOptions {
  accountId: string;
  ordersInterval?: number;
  accountInterval?: number;
  filters?: OrderFilterState;
  pagination?: {
    offset: number;
    limit: number;
  };
}

interface UseOrdersPollingResult {
  orders: OrderDetailResponse[];
  account: AccountDetailResponse | null;
  ordersLoading: boolean;
  accountLoading: boolean;
  ordersError: string | null;
  accountError: string | null;
  total: number;
  refreshOrders: () => Promise<void>;
  refreshAccount: () => Promise<void>;
  refreshAll: () => Promise<void>;
  pausePolling: () => void;
  resumePolling: () => void;
  isPaused: boolean;
}

/**
 * Map grouped filter values to actual status values for API.
 * Wire format is lowercase snake_case, matching backend serde rename_all.
 */
function mapStatusFilter(
  status: OrderFilterState['status']
): string | undefined {
  switch (status) {
    case 'all':
      return undefined;
    case 'open':
      return 'pending_new,accepted,new,partially_filled';
    case 'completed':
      return 'filled';
    case 'closed':
      return 'canceled,rejected,expired';
    default:
      return status;
  }
}

/**
 * Custom hook for polling orders and account data
 * Provides auto-refresh with configurable intervals
 * Pauses when tab is not visible or when explicitly paused
 */
export function useOrdersPolling({
  accountId,
  ordersInterval = 10000, // 10 seconds
  accountInterval = 30000, // 30 seconds
  filters = { status: 'all', symbol: '' },
  pagination = { offset: 0, limit: 25 },
}: UseOrdersPollingOptions): UseOrdersPollingResult {
  const [orders, setOrders] = useState<OrderDetailResponse[]>([]);
  const [account, setAccount] = useState<AccountDetailResponse | null>(null);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [accountLoading, setAccountLoading] = useState(true);
  const [ordersError, setOrdersError] = useState<string | null>(null);
  const [accountError, setAccountError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const ordersTimerRef = useRef<NodeJS.Timeout | null>(null);
  const accountTimerRef = useRef<NodeJS.Timeout | null>(null);
  const mountedRef = useRef(true);
  const ordersInitialLoadDoneRef = useRef(false);
  const accountInitialLoadDoneRef = useRef(false);

  const fetchOrders = useCallback(async () => {
    if (!accountId) return;

    try {
      // Only show loading spinner on initial load, not on polling refreshes
      if (!ordersInitialLoadDoneRef.current) {
        setOrdersLoading(true);
      }

      const params: Record<string, string> = {
        limit: pagination.limit.toString(),
        offset: pagination.offset.toString(),
      };

      // Add status filter
      const statusFilter = mapStatusFilter(filters.status);
      if (statusFilter) {
        params.status = statusFilter;
      }

      // Add symbol filter
      if (filters.symbol) {
        params.symbol = filters.symbol;
      }

      const response = await accountsApi.listOrders(accountId, params);

      if (mountedRef.current) {
        setOrders(response.orders);
        setTotal(response.total);
        setOrdersError(null);
        ordersInitialLoadDoneRef.current = true;
      }
    } catch (err) {
      if (mountedRef.current) {
        setOrdersError(
          err instanceof Error ? err.message : 'Failed to load orders'
        );
      }
    } finally {
      if (mountedRef.current) {
        setOrdersLoading(false);
      }
    }
  }, [accountId, filters.status, filters.symbol, pagination.offset, pagination.limit]);

  const fetchAccount = useCallback(async () => {
    if (!accountId) return;

    try {
      // Only show loading spinner on initial load, not on polling refreshes
      if (!accountInitialLoadDoneRef.current) {
        setAccountLoading(true);
      }

      const response = await accountsApi.getAccount(accountId);

      if (mountedRef.current) {
        setAccount(response);
        setAccountError(null);
        accountInitialLoadDoneRef.current = true;
      }
    } catch (err) {
      if (mountedRef.current) {
        setAccountError(
          err instanceof Error ? err.message : 'Failed to load account'
        );
      }
    } finally {
      if (mountedRef.current) {
        setAccountLoading(false);
      }
    }
  }, [accountId]);

  const refreshOrders = useCallback(async () => {
    setOrdersLoading(true);
    await fetchOrders();
  }, [fetchOrders]);

  const refreshAccount = useCallback(async () => {
    setAccountLoading(true);
    await fetchAccount();
  }, [fetchAccount]);

  const refreshAll = useCallback(async () => {
    await Promise.all([refreshOrders(), refreshAccount()]);
  }, [refreshOrders, refreshAccount]);

  const pausePolling = useCallback(() => {
    setIsPaused(true);
  }, []);

  const resumePolling = useCallback(() => {
    setIsPaused(false);
  }, []);

  // Initial fetch and reset on accountId change
  useEffect(() => {
    mountedRef.current = true;
    // Reset initial load flags when accountId changes
    ordersInitialLoadDoneRef.current = false;
    accountInitialLoadDoneRef.current = false;
    fetchOrders();
    fetchAccount();

    return () => {
      mountedRef.current = false;
    };
  }, [accountId]); // Only re-run on accountId change

  // Fetch orders when filters or pagination change
  useEffect(() => {
    fetchOrders();
  }, [filters.status, filters.symbol, pagination.offset, pagination.limit]);

  // Set up polling for orders
  useEffect(() => {
    if (isPaused) {
      if (ordersTimerRef.current) {
        clearInterval(ordersTimerRef.current);
        ordersTimerRef.current = null;
      }
      return;
    }

    ordersTimerRef.current = setInterval(() => {
      if (document.visibilityState === 'visible') {
        fetchOrders();
      }
    }, ordersInterval);

    return () => {
      if (ordersTimerRef.current) {
        clearInterval(ordersTimerRef.current);
      }
    };
  }, [fetchOrders, ordersInterval, isPaused]);

  // Set up polling for account
  useEffect(() => {
    if (isPaused) {
      if (accountTimerRef.current) {
        clearInterval(accountTimerRef.current);
        accountTimerRef.current = null;
      }
      return;
    }

    accountTimerRef.current = setInterval(() => {
      if (document.visibilityState === 'visible') {
        fetchAccount();
      }
    }, accountInterval);

    return () => {
      if (accountTimerRef.current) {
        clearInterval(accountTimerRef.current);
      }
    };
  }, [fetchAccount, accountInterval, isPaused]);

  // Pause polling when tab is hidden
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && !isPaused) {
        // Refresh immediately when tab becomes visible
        fetchOrders();
        fetchAccount();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fetchOrders, fetchAccount, isPaused]);

  return {
    orders,
    account,
    ordersLoading,
    accountLoading,
    ordersError,
    accountError,
    total,
    refreshOrders,
    refreshAccount,
    refreshAll,
    pausePolling,
    resumePolling,
    isPaused,
  };
}
